import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Select,
  Table,
  Tag as ANTDTag,
  Typography,
  Space,
  Tooltip,
} from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getBidderListSimple, getProjectListSimple } from '../../api/constlink';
import { ColumnsType } from 'antd/lib/table';
import { phoneFormatter } from '../../util/utils';
import { styledTable } from '../styled/mixin';

const StyledDiv = styled.div`
  ${styledTable}
`;
const { Text } = Typography;
const { Option } = Select;

const columns: ColumnsType<any> = [
  {
    title: '거래처명',
    ellipsis: true,
    dataIndex: ['vendor', 'application', 'general', 'name'],
    width: '10%',
    render: (value: any, record: any, index: number) => {
      return (
        <>
          {record.disabledReason === undefined ? (
            <Text>{value}</Text>
          ) : (
            <Tooltip title={record.disabledReason}>
              <Text type="danger">{value}</Text>
            </Tooltip>
          )}
        </>
      );
    },
  },
  {
    title: '대표명',
    dataIndex: ['vendor', 'application', 'general', 'representativeName'],
    width: '7%',
  },
  {
    title: '공정',
    dataIndex: ['vendor', 'gongjong'],
    width: '10%',
    render: (value: any, record: any, index: number) => {
      return (
        <>
          {value === undefined ? null : <ANTDTag key={index}>{value}</ANTDTag>}
        </>
      );
    },
  },
  {
    title: '주력분야',
    dataIndex: ['vendor', 'application', 'general', 'type3'],
    width: '15%',
    render: (value: any, record: any, index: number) => {
      return (
        <>
          {value === undefined
            ? null
            : value.map((tag: any, index: any) => (
                <ANTDTag key={index}>{tag}</ANTDTag>
              ))}
        </>
      );
    },
  },
  {
    title: '주소',
    dataIndex: ['vendor', 'application', 'general', 'companyAddress'],
    ellipsis: true,
    width: '20%',
  },
  {
    title: '담당자',
    align: 'center',
    children: [
      {
        title: '이름',
        dataIndex: ['vendor', 'application', 'contactPoint'],
        align: 'center',
        ellipsis: true,
        width: '6%',
        render: (value: any, record: any, index: number) => (
          <>{value?.name === undefined ? null : value.name}</>
        ),
      },
      {
        title: '전화번호',
        dataIndex: ['vendor', 'application', 'contactPoint'],
        align: 'center',
        ellipsis: true,
        width: '12%',
        render: (value: any, record: any, index: number) => (
          <>
            {value?.personalPhoneNumber === undefined
              ? null
              : `${phoneFormatter(value.personalPhoneNumber, 1)}`}
          </>
        ),
      },
      {
        title: '이메일',
        dataIndex: ['vendor', 'application', 'contactPoint'],
        align: 'center',
        ellipsis: true,
        width: '17%',
        render: (value: any, record: any, index: number) => (
          <>{value?.email === undefined ? null : value.email}</>
        ),
      },
    ],
  },
  {
    title: '평점',
    width: '5%',
    align: 'center',
    render: (value: any, record: any, index: number) => {
      const scoreSum = record.vendor.evaluation.reduce(
        (prev: any, next: any) => prev + next.score,
        0,
      );

      return (
        <>
          {record.vendor.evaluation.length === 0
            ? null
            : Math.floor(scoreSum / record.vendor.evaluation.length)}
        </>
      );
    },
  },
];

interface ImportBiddersModalProps {
  visible: boolean;
  enterpriseId: string;
  projectId: string;
  existingCollaborators: any[];
  onCreate: (userIdList: number[]) => void;
  onCancel: () => void;
}

function ImportBiddersModal({
  visible,
  enterpriseId,
  projectId,
  existingCollaborators,
  onCreate,
  onCancel,
}: ImportBiddersModalProps) {
  const [form] = Form.useForm();
  const [mount, setMount] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectOptions, setProjectOptions] = useState<any>();
  const [bidPackageOptions, setBidPackageOptions] = useState<any>();
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [selectedRowKeysLocal, setSelectedRowKeysLocal] = useState<React.Key[]>(
    [],
  );
  const [isSearchBtnEnabled, setIsSearchBtnEnabled] = useState(false);

  const [bidders, setBidders] = useState<any[]>([]);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setBidders([...selectedRows]);
      setSelectedRowKeysLocal(selectedRowKeys);
    },
    selectedRowKeys: selectedRowKeysLocal,
    getCheckboxProps: (record: any) => ({
      disabled: record.disabled,
    }),
  };

  const _getBidderListSimple = async (formData: any) => {
    const data: any[] = await getBidderListSimple({
      enterpriseId,
      projectId,
      bidPackageId: formData.bidPackage,
    });

    for (const _bidder of data) {
      let disabled = false;
      if (
        _bidder.vendor.application.contactPoint?.email === undefined ||
        _bidder.vendor.application.contactPoint?.personalPhoneNumber ===
          undefined
      ) {
        disabled = true;
        _bidder.disabledReason =
          '담당자의 이름,전화번호,이메일이 모두 존재해야 합니다.';
      }

      if (disabled === false) {
        for (const existingCollaborator of existingCollaborators) {
          if (existingCollaborator.vendor.id === _bidder.vendor.id) {
            disabled = true;
            _bidder.disabledReason = '이미 추가됨';
          }
        }
      }
      _bidder.disabled = disabled;
    }
    setCollaborators(data);
  };

  useEffect(() => {
    if (!mount) return;
    (async () => {
      let _projects = await getProjectListSimple({ enterpriseId });
      _projects = _projects.filter((data: any) => data.id !== +projectId);
      setProjects(_projects);

      let children: any = [];
      for (const project of _projects.sort((a: any, b: any) =>
        b.startDate.localeCompare(a.startDate),
      )) {
        children.push(
          <Option key={project.id} value={project.id}>
            {project.name} ({project.startDate} ~ {project.endDate})
          </Option>,
        );
      }
      setProjectOptions(children);
    })();
    return () => setMount(false);
  }, [enterpriseId, projectId, mount]);

  return (
    <StyledDiv>
      <Modal
        open={visible}
        title="업체 불러오기"
        okText="확인"
        cancelText="취소"
        width={1150}
        onCancel={onCancel}
        onOk={() => {
          form.validateFields().then((values) => {
            const _enterpriseIdList: number[] = [];
            for (const bidder of bidders) {
              _enterpriseIdList.push(bidder.vendor.id);
            }
            onCreate(_enterpriseIdList);
          });
        }}
      >
        {projectOptions && (
          <Row style={{ height: '20px' }} justify={'space-between'}>
            <Form
              form={form}
              onFinish={_getBidderListSimple}
              layout="inline"
              size="small"
              initialValues={{}}
            >
              <Space>
                <Col span={14}>
                  <Form.Item
                    name="project"
                    rules={[
                      {
                        required: true,
                        message: '프로젝트명을 선택 하세요.',
                      },
                    ]}
                  >
                    <Select
                      style={{ width: '400px' }}
                      placeholder="프로젝트를 선택하세요"
                      onSelect={async (key: any, option: any) => {
                        const children = [];
                        for (const project of projects) {
                          if (project.id === option.value) {
                            for (const bidPackage of project.bidPackages) {
                              children.push(
                                <Option
                                  key={bidPackage.id}
                                  value={bidPackage.id}
                                >
                                  {bidPackage.name}(
                                  {bidPackage.isBudgeting ? '견적' : '입찰'})
                                </Option>,
                              );
                            }
                            setBidPackageOptions(children);
                          }
                        }
                      }}
                    >
                      {projectOptions}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.project !== currentValues.project
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('project') !== undefined ? (
                        <Form.Item
                          name="bidPackage"
                          rules={[
                            {
                              required: true,
                              message: '견적요청을 선택 하세요.',
                            },
                          ]}
                        >
                          <Select
                            style={{ width: '200px' }}
                            placeholder="견적요청을 선택 하세요"
                            disabled={
                              form.getFieldValue('project') === undefined
                            }
                            onSelect={async (key: any, option: any) => {
                              setIsSearchBtnEnabled(true);
                            }}
                          >
                            {bidPackageOptions}
                          </Select>
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>
              </Space>
              <Col span={2}>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: '100px' }}
                    disabled={!isSearchBtnEnabled}
                  >
                    검색
                  </Button>
                </Form.Item>
              </Col>
            </Form>
          </Row>
        )}

        <Table
          rowKey="id"
          size="small"
          columns={columns}
          style={{ marginTop: '10px' }}
          rowSelection={{ ...rowSelection, checkStrictly: false }}
          dataSource={collaborators}
        />
      </Modal>
    </StyledDiv>
  );
}

export default ImportBiddersModal;
